/*
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/
import React, { useEffect, useMemo, useRef, useState, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  IconButton,
  TextareaAutosize,
  Typography,
  Switch,
  FormControlLabel,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../reducers';
import { startCompose, compose } from '../../../reducers/compose';
import { TranslateRequestDtoTargetLanguageEnum } from '@ink-ai/insight-service-sdk';
import { LoadingButton } from '@mui/lab';
import TranslateIcon from '@mui/icons-material/Translate';
import { copyText } from '@ink-ai/portal/common/utils';
import { CharacterCount } from '../../../common/components/CharacterCount';
import { translationActions } from '@ink-ai/portal/reducers/translation';
import { getAutoDirectionLanguages } from '@ink-ai/portal/common/utils/language';
import { makeRequest } from '@ink-ai/portal/common/requestHelper';

interface TextTranslateAreaProps {
  sourceLanguage: string;
  targetLanguage: string;
  languageDirection?: string; // 'auto' | 'en-zh' | 'zh-en'
  autoMerge: boolean;
}

const maxTextLength = 20000;

export const TextTranslateArea: React.FC<TextTranslateAreaProps> = ({
  sourceLanguage,
  targetLanguage,
  autoMerge,
  languageDirection = 'auto',
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<any>();
  const composeState = useSelector((state: RootState) => state.compose);
  const translationState = useSelector((state: RootState) => state.translation);
  const translatedText = composeState.generatedText;
  const [editedText, setEditedText] = useState<string>(translatedText);
  const sourceText = translationState.sourceText;
  const inputBox = useRef<HTMLTextAreaElement>(null);

  // Highlight alignment feature states
  const [highlightEnabled, setHighlightEnabled] = useState<boolean>(false);
  // 暂时关闭高亮模式下的右侧编辑功能（只读显示），后续需要时再打开
  const highlightEditingEnabled = false;
  const [sourceSegments, setSourceSegments] = useState<Array<{ text: string; index: number; startPos?: number; endPos?: number }>>([]);
  const [targetSegments, setTargetSegments] = useState<Array<{ text: string; index: number; alignedWith: number[]; startPos?: number; endPos?: number }>>([]);
  const [alignLoading, setAlignLoading] = useState<boolean>(false);
  const [alignError, setAlignError] = useState<string | null>(null);
  const [hoverSourceIndex, setHoverSourceIndex] = useState<number | null>(null);
  const [hoverTargetIndex, setHoverTargetIndex] = useState<number | null>(null);
  // Cache alignment results for the current source/target pair to avoid refetching
  const alignCacheRef = useRef<Map<string, { sourceSegments: Array<{ text: string; index: number; startPos?: number; endPos?: number }>; targetSegments: Array<{ text: string; index: number; alignedWith: number[]; startPos?: number; endPos?: number }> }>>(new Map());
  // 标记是否为新的翻译结果，仅在高亮开启且 isNew 为 true 时调用接口
  const [isNew, setIsNew] = useState<boolean>(false);
  // 编辑状态：编辑中不触发重新对齐，保持分段稳定
  const [isEditing, setIsEditing] = useState<boolean>(false);
  // 右侧高亮可编辑区域的 ref 与光标位置修复
  const editableRef = useRef<HTMLDivElement | null>(null);
  const caretOffsetRef = useRef<number | null>(null);
  // 覆盖层与文本域滚动同步及焦点管理
  const overlayScrollRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const isSyncingScrollRef = useRef<boolean>(false);
  // 右侧编辑状态：编辑期间隐藏覆盖层，以便自由定位与输入
  const [editingRight, setEditingRight] = useState<boolean>(false);
  // 右侧高亮容器，用于在退出编辑时同步父容器与覆盖层滚动偏移
  const rightHighlightContainerRef = useRef<HTMLDivElement | null>(null);
  // 右侧区域统一的滚动偏移缓存（覆盖层/父容器/文本域之间同步）
  const rightScrollOffsetRef = useRef<number>(0);
  // 左侧高亮容器与滚动缓存
  const sourceHighlightRef = useRef<HTMLDivElement | null>(null);
  const scrolledSourceRef = useRef<Set<number>>(new Set());
  const scrolledTargetRef = useRef<Set<number>>(new Set());
  const sourceHoverTimerRef = useRef<number | null>(null);
  const targetHoverTimerRef = useRef<number | null>(null);
  const lastScrollTsRef = useRef<{ left: number; right: number }>({ left: 0, right: 0 });
  const hoverDebounceMs = 150;

  const handleOverlayScroll = () => {
    if (!overlayScrollRef.current || !textareaRef.current) return;
    if (isSyncingScrollRef.current) return;
    isSyncingScrollRef.current = true;
    // 覆盖层滚动时更新统一偏移
    rightScrollOffsetRef.current = overlayScrollRef.current.scrollTop;
    textareaRef.current.scrollTop = overlayScrollRef.current.scrollTop;
    requestAnimationFrame(() => {
      isSyncingScrollRef.current = false;
    });
  };

  const handleTextareaScroll = () => {
    if (!overlayScrollRef.current || !textareaRef.current) return;
    if (isSyncingScrollRef.current) return;
    isSyncingScrollRef.current = true;
    if (overlayScrollRef.current) {
      overlayScrollRef.current.scrollTop = textareaRef.current.scrollTop;
    }
    // 文本域滚动时更新统一偏移
    rightScrollOffsetRef.current = textareaRef.current.scrollTop;
    requestAnimationFrame(() => {
      isSyncingScrollRef.current = false;
    });
  };

  // 编辑模式切换时，保持滚动位置：
  // - 进入编辑：将父容器和文本域滚动到覆盖层的当前偏移
  // - 退出编辑：将覆盖层滚动到父容器/文本域的当前偏移，并重置父容器滚动
  useEffect(() => {
    const offset = rightScrollOffsetRef.current || 0;
    if (editingRight) {
      // 进入编辑：父容器与文本域采用当前缓存偏移
      requestAnimationFrame(() => {
        if (rightHighlightContainerRef.current) rightHighlightContainerRef.current.scrollTop = offset;
        if (textareaRef.current) textareaRef.current.scrollTop = offset;
      });
    } else {
      // 退出编辑：覆盖层采用当前偏移，父容器重置为 0（避免双层滚动）
      requestAnimationFrame(() => {
        if (overlayScrollRef.current) overlayScrollRef.current.scrollTop = offset;
        if (textareaRef.current) textareaRef.current.scrollTop = offset;
        if (rightHighlightContainerRef.current) rightHighlightContainerRef.current.scrollTop = 0;
      });
    }
  }, [editingRight]);

  // 计算当前选区在容器文本中的字符偏移
  const getCaretOffset = (root: HTMLElement | null): number => {
    if (!root) return 0;
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return 0;
    const range = selection.getRangeAt(0);
    const preCaretRange = range.cloneRange();
    preCaretRange.selectNodeContents(root);
    try {
      preCaretRange.setEnd(range.endContainer, range.endOffset);
    } catch (_) {
      // 若 endContainer 已被重建，返回 0 以避免异常
      return 0;
    }
    return preCaretRange.toString().length;
  };

  // 计算点击位置在覆盖层整体文本中的绝对字符偏移
  const getGlobalTextOffset = (
    container: HTMLElement,
    node: Node,
    localOffset: number,
  ): number => {
    const walker = document.createTreeWalker(
      container,
      NodeFilter.SHOW_TEXT,
      null,
    );
    let total = 0;
    let current: Node | null = walker.nextNode();
    while (current) {
      if (current === node) {
        total += localOffset;
        break;
      } else {
        const text = (current.nodeValue ?? '');
        total += text.length;
      }
      current = walker.nextNode();
    }
    return total;
  };

  // 根据字符偏移恢复光标位置
  const setCaretOffset = (root: HTMLElement | null, offset: number) => {
    if (!root) return;
    const range = document.createRange();
    const selection = window.getSelection();
    let current = 0;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let node = walker.nextNode() as Text | null;
    while (node) {
      const len = node.nodeValue ? node.nodeValue.length : 0;
      if (current + len >= offset) {
        range.setStart(node, Math.max(0, offset - current));
        range.collapse(true);
        if (selection) {
          selection.removeAllRanges();
          selection.addRange(range);
        }
        return;
      }
      current += len;
      node = walker.nextNode() as Text | null;
    }
    // 若偏移超出长度，定位到结尾
    const endRange = document.createRange();
    endRange.selectNodeContents(root);
    endRange.collapse(false);
    if (selection) {
      selection.removeAllRanges();
      selection.addRange(endRange);
    }
  };
  const isElementFullyVisible = (container: HTMLElement, el: HTMLElement): boolean => {
    const cRect = container.getBoundingClientRect();
    const eRect = el.getBoundingClientRect();
    const margin = 2;
    return eRect.top >= cRect.top + margin && eRect.bottom <= cRect.bottom - margin;
  };

  // Endpoint for sentence alignment; adjust to match backend spec
  const ALIGN_API_PATH = '/api/v1/text-generation/hyperText';

  // 计算文本更改的起始位置与长度差，用于稳定分段边界
  const computeTextChange = (prev: string, next: string): { delta: number; startIndex: number } => {
    const lenPrev = prev.length;
    const lenNext = next.length;
    const minLen = Math.min(lenPrev, lenNext);
    let start = 0;
    while (start < minLen && prev.charCodeAt(start) === next.charCodeAt(start)) start++;
    const delta = lenNext - lenPrev;
    return { delta, startIndex: start };
  };

  // 在本地根据字符增减稳定调整分段边界（保持分段ID不变）
  const adjustSegmentsForChange = (
    prevSegs: Array<{ text: string; index: number; alignedWith: number[]; startPos?: number; endPos?: number }>,
    prevTextLength: number,
    change: { delta: number; startIndex: number },
  ): Array<{ text: string; index: number; alignedWith: number[]; startPos?: number; endPos?: number }> => {
    const { delta, startIndex } = change;
    if (!delta) return prevSegs;
    const segs = prevSegs.map((s) => ({ ...s }));
    let idx = segs.findIndex((s) => {
      const start = s.startPos ?? 0;
      const endExclusive = Math.min((s.endPos ?? 0) + 1, prevTextLength);
      return startIndex >= start && startIndex <= endExclusive;
    });
    if (idx < 0) idx = segs.length - 1;
    const clampInclusive = (val: number, min: number, max: number) => {
      if (val < min) return min;
      if (val > max) return max;
      return val;
    };
    const startI = segs[idx].startPos ?? 0;
    const endI = segs[idx].endPos ?? Math.max(prevTextLength - 1, startI);
    segs[idx].endPos = clampInclusive(endI + delta, startI, Number.MAX_SAFE_INTEGER);
    for (let j = idx + 1; j < segs.length; j++) {
      const sj = segs[j];
      const s0 = (sj.startPos ?? 0) + delta;
      const e0 = (sj.endPos ?? Math.max(prevTextLength - 1, s0 - delta)) + delta;
      sj.startPos = clampInclusive(s0, 0, Number.MAX_SAFE_INTEGER);
      sj.endPos = clampInclusive(e0, sj.startPos ?? 0, Number.MAX_SAFE_INTEGER);
    }
    return segs;
  };

  const isInputTextValid = useMemo(() => {
    if (!sourceText || sourceText.length === 0) {
      return false;
    }
    if (sourceText.length > maxTextLength) {
      return false;
    }
    return true;
  }, [sourceText]);

  useEffect(() => {
    return () => {
      dispatch(compose.actions.clearAll());
    };
  }, []);

  useEffect(() => {
    setEditedText(translatedText);
  }, [translatedText]);

  // 高亮开关开启后，直接调用对齐接口（始终刷新对照），不再依赖 isNew/isEditing 判断
  useEffect(() => {
    const shouldAlign = highlightEnabled && !!sourceText && !!editedText;
    if (!shouldAlign) {
      if (!highlightEnabled) {
        setAlignError(null);
        setHoverSourceIndex(null);
        setHoverTargetIndex(null);
      }
      return;
    }

    const key = `${sourceText}|||${editedText}`;
    const fetchAlignments = async () => {
      try {
        setAlignLoading(true);
        setAlignError(null);
        const data: any = await makeRequest({
          url: ALIGN_API_PATH,
          method: 'POST',
          data: {
            originText: sourceText,
            targetText: editedText,
            // autoMerge: autoMerge, // 暂时不传递该参数
          },
        });
        const payload = data?.payload ?? {};
        const src = Array.isArray(payload?.sourceSentences) ? payload.sourceSentences : [];
        const tgt = Array.isArray(payload?.targetSentences) ? payload.targetSentences : [];
        const normSrc = src
          .filter((s: any) => typeof s?.text === 'string')
          .map((s: any) => ({
            text: s.text,
            index: Number(s.index ?? 0),
            startPos: s.startPos,
            endPos: s.endPos,
          }));
        const normTgt = tgt
          .filter((t: any) => typeof t?.text === 'string')
          .map((t: any) => ({
            text: t.text,
            index: Number(t.index ?? 0),
            alignedWith: Array.isArray(t.alignedWith)
              ? t.alignedWith.map((x: any) => Number(x))
              : [],
            startPos: t.startPos,
            endPos: t.endPos,
          }));
        setSourceSegments(normSrc);
        setTargetSegments(normTgt);
        // 仍保留缓存更新，但不用于跳过调用
        alignCacheRef.current.set(key, {
          sourceSegments: normSrc,
          targetSegments: normTgt,
        });
      } catch (err: any) {
        setAlignError('获取对齐结果失败，请稍后重试');
        setSourceSegments([]);
        setTargetSegments([]);
      } finally {
        setAlignLoading(false);
      }
    };
    fetchAlignments();
  }, [highlightEnabled, sourceText, editedText]);

  // 当源文本或目标文本变化（例如再次点击翻译），清理悬浮状态；缓存按键值保留，避免无意义清除
  useEffect(() => {
    setHoverSourceIndex(null);
    setHoverTargetIndex(null);
    setAlignError(null);
  }, [sourceText, editedText]);

  // 在高亮编辑文本或分段更新后，使用记录的偏移恢复光标位置，避免跳动
  useLayoutEffect(() => {
    if (!highlightEnabled) return;
    const root = editableRef.current;
    const offset = caretOffsetRef.current;
    if (!root || offset === null) return;
    // 确保在 DOM 更新绘制后恢复选区，避免再次被覆盖
    requestAnimationFrame(() => {
      setCaretOffset(root, offset);
    });
  }, [editedText, targetSegments, highlightEnabled]);

  // 切换高亮或分段变化时不缓存滚动，始终按需滚动到对应句子
  useEffect(() => {
    // no-op: 移除滚动缓存逻辑，避免手动滚动后悬停不再自动滚动
  }, [highlightEnabled, sourceSegments, targetSegments]);

  // 悬停源句子时，将右侧对应目标句滚动到视口（防抖，仅在不在视口时滚动）
  useEffect(() => {
    if (!highlightEnabled || hoverSourceIndex === null) return;
    if (sourceHoverTimerRef.current) {
      clearTimeout(sourceHoverTimerRef.current);
      sourceHoverTimerRef.current = null;
    }
    sourceHoverTimerRef.current = window.setTimeout(() => {
      const target = targetSegments.find((t) => Array.isArray(t.alignedWith) && t.alignedWith.includes(hoverSourceIndex));
      if (!target) return;
      const container = rightHighlightContainerRef.current;
      if (!container) return;
      if (Date.now() - lastScrollTsRef.current.right < hoverDebounceMs) return;
      const el = container.querySelector(`#tgt-seg-${target.index}`) as HTMLElement | null;
      if (!el) return;
      if (!isElementFullyVisible(container, el)) {
        el.scrollIntoView({ block: 'nearest' });
      }
    }, hoverDebounceMs);
    return () => {
      if (sourceHoverTimerRef.current) {
        clearTimeout(sourceHoverTimerRef.current);
        sourceHoverTimerRef.current = null;
      }
    };
  }, [hoverSourceIndex, highlightEnabled, targetSegments]);

  // 悬停目标句子时，将左侧对应源句滚动到视口（防抖，仅在不在视口时滚动）
  useEffect(() => {
    if (!highlightEnabled || hoverTargetIndex === null) return;
    if (targetHoverTimerRef.current) {
      clearTimeout(targetHoverTimerRef.current);
      targetHoverTimerRef.current = null;
    }
    targetHoverTimerRef.current = window.setTimeout(() => {
      const tgt = targetSegments.find((t) => t.index === hoverTargetIndex);
      const sourceIndex = tgt && Array.isArray(tgt.alignedWith) ? tgt.alignedWith[0] : undefined;
      if (sourceIndex === undefined) return;
      const container = sourceHighlightRef.current;
      if (!container) return;
      if (Date.now() - lastScrollTsRef.current.left < hoverDebounceMs) return;
      const el = container.querySelector(`#src-seg-${sourceIndex}`) as HTMLElement | null;
      if (!el) return;
      if (!isElementFullyVisible(container, el)) {
        el.scrollIntoView({ block: 'nearest' });
      }
    }, hoverDebounceMs);
    return () => {
      if (targetHoverTimerRef.current) {
        clearTimeout(targetHoverTimerRef.current);
        targetHoverTimerRef.current = null;
      }
    };
  }, [hoverTargetIndex, highlightEnabled, targetSegments, sourceSegments]);

  // Helpers: render inline highlighted text based on sentence positions
  const renderInlineSource = () => {
    const nodes: React.ReactNode[] = [];
    if (!sourceText || sourceSegments.length === 0) {
      nodes.push(
        <span key="src-all" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {sourceText}
        </span>,
      );
      return nodes;
    }
    const sorted = [...sourceSegments].sort((a, b) => (a.startPos ?? 0) - (b.startPos ?? 0));
    let cursor = 0;
    sorted.forEach((seg) => {
      const start = Math.max(0, Number(seg.startPos ?? cursor));
      const rawEnd = Number(seg.endPos ?? start);
      // API 的 endPos 为“包含末位”，因此需要 +1 转为 slice 的排他上界
      const endExclusive = Math.min(sourceText.length, rawEnd + 1);
      // gap before segment
      if (start > cursor) {
        nodes.push(
          <span key={`src-gap-${cursor}`} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {sourceText.slice(cursor, start)}
          </span>,
        );
      }
      const isHovered = hoverSourceIndex === seg.index;
      const isAlignedToHoveredTarget =
        hoverTargetIndex !== null &&
        targetSegments.some((t) => t.index === hoverTargetIndex && t.alignedWith?.includes(seg.index));
      const bg = isHovered || isAlignedToHoveredTarget ? 'rgba(255, 230, 150, 0.7)' : 'transparent';
      nodes.push(
        <span
          key={`src-${seg.index}`}
          id={`src-seg-${seg.index}`}
          onMouseEnter={() => setHoverSourceIndex(seg.index)}
          onMouseLeave={() => setHoverSourceIndex(null)}
          style={{
            backgroundColor: bg,
            borderRadius: 4,
            transition: 'background-color 0.1s ease-in-out',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {sourceText.slice(start, endExclusive)}
        </span>,
      );
      cursor = endExclusive;
    });
    // trailing text
    if (cursor < sourceText.length) {
      nodes.push(
        <span key={`src-gap-${cursor}-end`} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {sourceText.slice(cursor)}
        </span>,
      );
    }
    return nodes;
  };

  const renderInlineTarget = () => {
    const nodes: React.ReactNode[] = [];
    if (!editedText || targetSegments.length === 0) {
      nodes.push(
        <span key="tgt-all" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {editedText}
        </span>,
      );
      return nodes;
    }
    const sorted = [...targetSegments].sort((a, b) => (a.startPos ?? 0) - (b.startPos ?? 0));
    let cursor = 0;
    sorted.forEach((seg) => {
      const sPos = Number(seg.startPos);
      const ePos = Number(seg.endPos);
      if (!Number.isFinite(sPos) || !Number.isFinite(ePos)) {
        return;
      }
      const start = Math.max(0, sPos);
      const rawEnd = ePos;
      const endExclusive = Math.min(editedText.length, rawEnd + 1);

      if (start > cursor) {
        nodes.push(
          <span key={`tgt-gap-${cursor}`} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {editedText.slice(cursor, start)}
          </span>,
        );
      }

      // 跳过空文本段（例如 API 返回 text=="" 的占位分段），避免误渲染 1 个字符
      if (!seg.text || seg.text.length === 0 || endExclusive <= start) {
        return;
      }

      const isHovered = hoverTargetIndex === seg.index;
      const isAlignedToHoveredSource = hoverSourceIndex !== null && seg.alignedWith?.includes(hoverSourceIndex);
      const bg = isHovered || isAlignedToHoveredSource ? 'rgba(255, 230, 150, 0.7)' : 'transparent';
      nodes.push(
        <span
          key={`tgt-${seg.index}`}
          id={`tgt-seg-${seg.index}`}
          onMouseEnter={() => setHoverTargetIndex(seg.index)}
          onMouseLeave={() => setHoverTargetIndex(null)}
          style={{
            backgroundColor: bg,
            borderRadius: 4,
            transition: 'background-color 0.1s ease-in-out',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {editedText.slice(start, endExclusive)}
        </span>,
      );
      cursor = endExclusive;
    });
    return nodes;
  };

  const handleSourceTextChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    const nextValue = event.target.value ?? '';
    const capped = nextValue.length > maxTextLength
      ? nextValue.slice(0, maxTextLength)
      : nextValue;
    dispatch(translationActions.setSourceText(capped));
  };

  // 自动合并换行：当 autoMerge 开启时，粘贴文本逐行检查是否为句子结束
  // 若上一行末尾没有句号/叹号，则去掉换行并与下一行合并；否则保留换行
  const SENTENCE_END_RE = /[.!。！]\s*$/;
  const autoMergeClipboardText = (text: string): string => {
    const lines = text.split(/\r?\n/);
    if (lines.length <= 1) return text;
    let buffer = lines[0] ?? '';
    const result: string[] = [];
    for (let i = 1; i < lines.length; i++) {
      const next = lines[i] ?? '';
      const prevEnds = SENTENCE_END_RE.test(buffer);
      if (prevEnds) {
        // 保留换行（作为段落边界）
        result.push(buffer);
        buffer = next;
      } else {
        // 合并：移除换行，必要时插入空格避免词粘连
        const needsSpace = !/\s$/.test(buffer) && !/^\s/.test(next);
        buffer = needsSpace ? `${buffer} ${next}` : `${buffer}${next}`;
      }
    }
    result.push(buffer);
    // 保留句末换行（即由句号/叹号决定的段落换行）
    return result.join('\n');
  };

  const handleSourcePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    if (!autoMerge) return; // 未开启自动合并时遵循默认粘贴行为
    e.preventDefault();
    const plain = e.clipboardData.getData('text/plain') ?? '';
    const processed = autoMergeClipboardText(plain);
    const el = e.currentTarget;
    const start = el.selectionStart ?? sourceText.length;
    const end = el.selectionEnd ?? start;
    let newValue = (sourceText ?? '').slice(0, start) + processed + (sourceText ?? '').slice(end);
    if (newValue.length > maxTextLength) {
      newValue = newValue.slice(0, maxTextLength);
    }
    dispatch(translationActions.setSourceText(newValue));
    const newCaret = Math.min(start + processed.length, newValue.length);
    // 更新光标位置到粘贴末尾，保持良好编辑体验
    requestAnimationFrame(() => {
      const ta = inputBox.current;
      if (ta) {
        try {
          ta.setSelectionRange(newCaret, newCaret);
        } catch (_) {
          // 某些环境下 TextareaAutosize 的 setSelectionRange 可能不可用，忽略错误
        }
      }
    });
  };

  const handleCopyText = async (text: string) => {
    copyText(text);
  };

  const handleTranslate = () => {
    // Auto direction: detect primary language and set appropriate source/target
    let effectiveSource = sourceLanguage as TranslateRequestDtoTargetLanguageEnum;
    let effectiveTarget = targetLanguage as TranslateRequestDtoTargetLanguageEnum;

    if (languageDirection === 'auto' && sourceText) {
      const auto = getAutoDirectionLanguages(sourceText);
      if (auto) {
        effectiveSource = auto.source as TranslateRequestDtoTargetLanguageEnum;
        effectiveTarget = auto.target as TranslateRequestDtoTargetLanguageEnum;
      }
    }

    dispatch(
      startCompose({
        task: 'translate',
        text: sourceText,
        sourceLanguage: effectiveSource,
        targetLanguage: effectiveTarget,
        glossaryIdList: translationState.selectedGlossaries,
        referenceStoreIdList: translationState.selectedReferenceStores,
        glossariesFolders: translationState.selectedGlossariesFolders,
        referenceStoreFolders: translationState.selectedReferenceStoreFolders,
        // autoMerge, // 暂时不传递该参数
      }),
    );
    // 标记为新结果，下次开启高亮时触发对齐
    setIsNew(true);
    // 点击翻译后默认关闭高亮，避免旧分割干扰并防止不必要的请求
    setHighlightEnabled(false);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          position: 'relative',
          cursor: 'text',
        }}
      >
        {/* Left: source text or aligned view */}
        <Box sx={{ position: 'relative', width: '100%' }}>
          {highlightEnabled && sourceSegments.length > 0 ? (
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                height: 'calc(100vh - 350px)',
                overflow: 'auto',
                border: '1px solid #ccc',
                padding: '16.5px 14px',
              }}
              ref={sourceHighlightRef}
              onScroll={() => {
                lastScrollTsRef.current.left = Date.now();
              }}
            >
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', textAlign: 'left', fontSize: '16px', lineHeight: '24px' }}>
                {renderInlineSource()}
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                height: 'calc(100vh - 350px)',
                overflow: 'auto',
                border: '1px solid #ccc',
                padding: '16.5px 14px',
                'textarea:focus': {
                  outline: 'none',
                  border: 'none',
                },
              }}
              onClick={() => inputBox.current?.focus()}
            >
              <TextareaAutosize
                style={{
                  width: '100%',
                  resize: 'none',
                  fontSize: '16px',
                  lineHeight: '24px',
                }}
                ref={inputBox}
                value={sourceText}
                maxLength={maxTextLength}
                onChange={handleSourceTextChange}
                onPaste={handleSourcePaste}
              />
            </Box>
          )}
          {sourceText && sourceText.length > 0 && (
            <IconButton
              aria-label="clear-source"
              onClick={() => {
                dispatch(translationActions.setSourceText(''));
                setEditedText('');
                dispatch(compose.actions.clearAll());
                setHighlightEnabled(false);
                // 清理悬浮与对齐状态
                setHoverSourceIndex(null);
                setHoverTargetIndex(null);
                setAlignError(null);
                setSourceSegments([]);
                setTargetSegments([]);
              }}
              sx={{ position: 'absolute', top: 8, right: 8, zIndex: 3 }}
            >
              <CloseIcon />
            </IconButton>
          )}
          <Typography
            sx={{
              position: 'absolute',
              bottom: 12,
              right: 12,
              zIndex: 2,
              pointerEvents: 'none',
              color: sourceText.length > maxTextLength ? 'red' : 'gray',
            }}
          >
            <CharacterCount current={sourceText.length} max={maxTextLength} />
          </Typography>
        </Box>

        {/* Right: translation text or aligned view */}
        <Box sx={{ position: 'relative', width: '100%' }}>
          {highlightEnabled ? (
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                height: 'calc(100vh - 350px)',
                overflow: 'auto',
                border: '1px solid #ccc',
                backgroundColor: '#f5f5f5',
                padding: '16.5px 14px',
              }}
              ref={rightHighlightContainerRef}
              onScroll={() => {
                rightScrollOffsetRef.current = rightHighlightContainerRef.current?.scrollTop || 0;
                lastScrollTsRef.current.right = Date.now();
              }}
            >
              {alignError && (
                <Typography color="error" sx={{ mb: 1 }}>
                  {alignError}
                </Typography>
              )}
              {alignLoading && (
                <Typography sx={{ mb: 1, color: 'gray' }}>
                  {t('translation.alignments.loading')}
                </Typography>
              )}
              {highlightEditingEnabled ? (
                <div
                  contentEditable
                  suppressContentEditableWarning={true}
                  role="textbox"
                  aria-multiline="true"
                  spellCheck={false}
                  ref={editableRef}
                  onFocus={() => setIsEditing(true)}
                  onBlur={() => setIsEditing(false)}
                  onBeforeInput={(e) => {
                    const anyEv: any = e;
                    const type: string = anyEv?.nativeEvent?.inputType || anyEv?.inputType || '';
                    const data: string = anyEv?.data || '';
                    let delta = 0;
                    if (type && type.startsWith('delete')) {
                      delta = -Math.max(1, data?.length || 1);
                    } else {
                      delta = data?.length || 0;
                    }
                    if (!delta || !editableRef.current) return;
                    const startIndex = getCaretOffset(editableRef.current);
                    const nextLen = editedText.length + delta;
                    const nextOffset = Math.max(0, Math.min(nextLen, startIndex + delta));
                    caretOffsetRef.current = nextOffset;
                  }}
                  onInput={(e) => {
                    const target = e.currentTarget as HTMLDivElement;
                    const next = target.innerText ?? '';
                    const prev = editedText;
                    const prevLen = prev.length;
                    const nextLen = next.length;
                    let startIndex = 0;
                    const minLen = Math.min(prevLen, nextLen);
                    while (startIndex < minLen && prev.charCodeAt(startIndex) === next.charCodeAt(startIndex)) {
                      startIndex++;
                    }
                    let endPrev = prevLen;
                    let endNext = nextLen;
                    while (endPrev > startIndex && endNext > startIndex && prev.charCodeAt(endPrev - 1) === next.charCodeAt(endNext - 1)) {
                      endPrev--;
                      endNext--;
                    }
                    const delta = nextLen - prevLen;
                    setEditedText(next);
                    const desiredOffset = Math.max(
                      0,
                      Math.min(
                        nextLen,
                        delta < 0 ? startIndex : startIndex + delta,
                      ),
                    );
                    caretOffsetRef.current = desiredOffset;
                    requestAnimationFrame(() => {
                      setTargetSegments((prevSegs) => adjustSegmentsForChange(prevSegs, prevLen, { delta, startIndex }));
                    });
                  }}
                  style={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontSize: '16px',
                    lineHeight: 1.5,
                    color: '#000',
                    textAlign: 'left',
                    outline: 'none',
                  }}
                >
                  {renderInlineTarget()}
                </div>
              ) : (
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '16px', lineHeight: '24px', textAlign: 'left', color: '#000' }}>
                  {renderInlineTarget()}
                </Typography>
              )}
            </Box>
          ) : (
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                height: 'calc(100vh - 350px)',
                overflow: 'auto',
                border: '1px solid #ccc',
                padding: '16.5px 14px',
                backgroundColor: '#f5f5f5',
                'textarea:focus': {
                  outline: 'none',
                  border: 'none',
                },
              }}
            >
              <TextareaAutosize
                style={{
                  width: '100%',
                  resize: 'none',
                  backgroundColor: '#f5f5f5',
                  fontSize: '16px',
                }}
                placeholder={t('translation.text.placeholder')}
                value={editedText}
                onChange={(e) => setEditedText(e.target.value)}
              />
            </Box>
          )}
          <IconButton
            onClick={() => handleCopyText(editedText)}
            sx={{
              position: 'absolute',
              bottom: 8,
              right: 8,
              zIndex: 3,
            }}
          >
            <ContentCopyIcon />
          </IconButton>
        </Box>
      </Box>
      <Box sx={{display: 'flex', justifyContent: 'flex-end'}}>
        
        {/* Toggle to enable/disable sentence Source-Target Highlighting */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1. , mr: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={highlightEnabled}
                onChange={(e) => setHighlightEnabled(e.target.checked)}
                disabled={!translatedText || translatedText.length === 0 || composeState.isGenerating}
              />
            }
            label={highlightEnabled ? t('translation.text.highlight.disable') : t('translation.text.highlight.enable')}
          />
        </Box>

        <LoadingButton
          variant="contained"
          loadingPosition="start"
          color="primary"
          onClick={handleTranslate}
          sx={{ alignSelf: 'flex-end', mt: 1, textTransform: 'none' }}
          disabled={!isInputTextValid || composeState.isGenerating}
          loading={composeState.isGenerating}
          startIcon={<TranslateIcon />}
        >
          {t('translation.text.translate')}
        </LoadingButton>
      </Box>
    </Box>
  );
};
